import { useState, useEffect, useRef } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useLocation } from "wouter";
import { Camera, Save, Loader2, AlertCircle, CheckCircle2, User, Mail, Building2, MapPin, Briefcase, Calendar, ShieldCheck, ZoomIn, ZoomOut, Crop, X, ArrowLeft } from "lucide-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const BANDS = ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d", "Band 9", "Other"];

function cropToCircle(base64: string, scale: number, offsetX: number, offsetY: number, size: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d")!;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();
      const scaledW = img.width * scale;
      const scaledH = img.height * scale;
      const x = (size - scaledW) / 2 + offsetX;
      const y = (size - scaledH) / 2 + offsetY;
      ctx.drawImage(img, x, y, scaledW, scaledH);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = base64;
  });
}

export default function ProfileEditor() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const [, setLocation] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [siteName, setSiteName] = useState("");
  const [band, setBand] = useState("");
  const [jobRole, setJobRole] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");

  const [cropModal, setCropModal] = useState(false);
  const [cropImage, setCropImage] = useState("");
  const [cropScale, setCropScale] = useState(1);
  const [cropOffsetX, setCropOffsetX] = useState(0);
  const [cropOffsetY, setCropOffsetY] = useState(0);
  const [cropSize] = useState(300);
  const [cropLoading, setCropLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!isSignedIn) return;
    fetch(`${basePath}/api/user/profile`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setProfile(data);
        setFirstName(data.firstName || "");
        setLastName(data.lastName || "");
        setDob(data.dateOfBirth || "");
        setWorkEmail(data.workEmail || "");
        setOrgName(data.organisationName || "");
        setSiteName(data.siteName || "");
        setBand(data.band || "");
        setJobRole(data.jobRole || "");
        setPhotoUrl(data.profilePhotoUrl || "");
      })
      .catch(() => setError("Failed to load profile"))
      .finally(() => setLoading(false));
  }, [isSignedIn]);

  const dashboardUrl = profile?.role ? `/${profile.role}` : "/nurse";

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Photo must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result as string);
      setCropScale(1);
      setCropOffsetX(0);
      setCropOffsetY(0);
      setCropModal(true);
      setError(null);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropSave = async () => {
    setCropLoading(true);
    try {
      const cropped = await cropToCircle(cropImage, cropScale, cropOffsetX, cropOffsetY, cropSize);
      const res = await fetch(`${basePath}/api/user/profile`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ profilePhotoUrl: cropped }),
      });
      if (res.ok) {
        setPhotoUrl(cropped);
        setCropModal(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError("Failed to save photo");
      }
    } catch { setError("Upload failed"); }
    finally { setCropLoading(false); }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setDragStart({ x: e.clientX - cropOffsetX, y: e.clientY - cropOffsetY });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setCropOffsetX(e.clientX - dragStart.x);
    setCropOffsetY(e.clientY - dragStart.y);
  };
  const handleMouseUp = () => setDragging(false);

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const res = await fetch(`${basePath}/api/user/profile`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          firstName, lastName, dateOfBirth: dob,
          organisationName: orgName, siteName,
          band, jobRole,
        }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        const data = await res.json();
        setError(data.error || "Failed to save");
      }
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  };

  if (!isSignedIn) return null;
  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Profile Settings</h1>
              <p className="text-sm text-slate-500">Manage your profile details and photo</p>
            </div>
          </div>
          <a href={dashboardUrl} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-teal-600 transition-colors bg-white border border-slate-200 rounded-full px-4 py-2 hover:border-teal-300 shadow-sm">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </a>
        </div>

        {error && (
          <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {saved && (
          <div className="mb-6 p-3 rounded-lg bg-green-50 border border-green-200 flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
            <p className="text-sm text-green-700">Profile updated successfully</p>
          </div>
        )}

        {/* Crop Modal */}
        {cropModal && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Crop className="h-5 w-5 text-teal-600" /> Crop Profile Photo
                </h2>
                <button onClick={() => setCropModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-center bg-slate-900 rounded-xl overflow-hidden mb-4"
                  style={{ height: 300 }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <div
                    className="relative overflow-hidden rounded-full border-4 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"
                    style={{ width: 250, height: 250, cursor: dragging ? "grabbing" : "grab" }}
                  >
                    <img
                      src={cropImage}
                      alt="Crop preview"
                      draggable={false}
                      style={{
                        transform: `scale(${cropScale}) translate(${cropOffsetX / cropScale}px, ${cropOffsetY / cropScale}px)`,
                        transformOrigin: "center",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <ZoomOut className="h-4 w-4 text-slate-400" />
                  <input type="range" min="0.5" max="3" step="0.05" value={cropScale}
                    onChange={(e) => setCropScale(parseFloat(e.target.value))}
                    className="flex-1 accent-teal-600" />
                  <ZoomIn className="h-4 w-4 text-slate-400" />
                  <span className="text-xs text-slate-400 w-10 text-right">{Math.round(cropScale * 100)}%</span>
                </div>
                <p className="text-xs text-slate-400 text-center mb-4">Drag to position, zoom to fit the circle</p>
                <div className="flex gap-3">
                  <button onClick={() => setCropModal(false)}
                    className="flex-1 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleCropSave} disabled={cropLoading}
                    className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                    {cropLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crop className="h-4 w-4" />}
                    Crop & Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Camera className="h-5 w-5 text-teal-600" /> Profile Photo</h2>
            <div className="flex items-center gap-6">
              <div className="relative">
                {photoUrl ? (
                  <img src={photoUrl} alt="Profile" className="h-24 w-24 rounded-full object-cover border-4 border-teal-100" />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-teal-50 border-4 border-teal-100 flex items-center justify-center">
                    <User className="h-10 w-10 text-teal-400" />
                  </div>
                )}
              </div>
              <div>
                <button onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors">
                  {photoUrl ? "Change Photo" : "Upload Photo"}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                <p className="text-xs text-slate-400 mt-2">JPG or PNG, max 5MB</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><User className="h-5 w-5 text-teal-600" /> Personal Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1"><Calendar className="h-4 w-4" /> Date of Birth</label>
                <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Briefcase className="h-5 w-5 text-teal-600" /> Professional Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1"><Building2 className="h-4 w-4" /> Employer</label>
                <input type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1"><MapPin className="h-4 w-4" /> Site</label>
                <input type="text" value={siteName} onChange={(e) => setSiteName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Band / Grade</label>
                  <select value={band} onChange={(e) => setBand(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none bg-white">
                    <option value="">Select...</option>
                    {BANDS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Job Role</label>
                  <input type="text" value={jobRole} onChange={(e) => setJobRole(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 outline-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-600" /> Verified Credentials</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Work Email</span>
                <span className="text-slate-900 font-medium">{profile?.workEmail || "—"} {profile?.workEmailVerified ? "✅" : ""}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Registration</span>
                <span className="text-slate-900 font-medium">{profile?.registrationBody?.toUpperCase()}: {profile?.registrationNumber || "—"}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Role</span>
                <span className="text-slate-900 font-medium capitalize">{profile?.role || "—"}</span>
              </div>
            </div>
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full py-3.5 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
